import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AlertCircle, AlertTriangle, Plus, Pencil, Trash2, Loader2, Download } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  FormField,
  StatusBadge,
  NotificationCard,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
} from '@afrilink/ui';
import { useDnsZone } from '../hooks/useDnsZone';
import { domainService } from '../services/domainService';
import type {
  DnsZoneRecord,
  DnsRecordTypeFull,
  DnsMutationRecord,
} from '../types/dns.types';
import { downloadZoneFile, downloadZoneCsv, isExportableRecord } from '../utils/dnsExport';

const EDITABLE_TYPES: DnsRecordTypeFull[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];

/**
 * DnsEditor — shared editor used by both the customer-side DomainDnsTab
 * and the admin-side DNS section on AdminDomainDetailPage.
 *
 * G1 (Path P1, cPanel-backed). Per locked decisions §G1:
 *   - Q1 apex A: warn but allow
 *   - Q2 customer NS: read-only at apex; admin can edit anything
 *   - Q4 DNSSEC: out of scope
 *   - Q7 customer audit visibility: customer sees their own audit on this tab
 *
 * `mode='customer'` enforces the customer-only restrictions (no apex NS edit,
 * no destructive bulk actions). `mode='admin'` lifts those.
 */
export function DnsEditor({
  domain,
  mode,
}: {
  domain: string;
  mode: 'customer' | 'admin';
}) {
  const { toast } = useToast();
  const { data, isLoading, error, refetch } = useDnsZone(domain);
  const [editing, setEditing] = useState<DnsZoneRecord | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<DnsZoneRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const records = useMemo(() => {
    const all = data?.records ?? [];
    // Hide SOA and the auto-generated apex NS records — they aren't useful
    // to edit and clutter the UI. Customers never edit them; admins manage
    // NS via the registrar Nameservers tab.
    //
    // 2026-07-26: also drop WHM's pseudo-type lines (':RAW' for comments and
    // blank lines, '$TTL' directives). `dumpzone` returns every LINE of the
    // zone file, not just resource records, and parseRawRecord passes those
    // through with an empty value. They rendered as meaningless rows here and
    // broke the zone export (Cloudflare: "unknown RR type: ':RAW'").
    // isExportableRecord is the single rule, shared with dnsExport.
    return all.filter(isExportableRecord);
  }, [data]);

  const editable = data?.editable ?? false;
  const mxRecords = records.filter((r) => r.type === 'MX');

  function isApex(name: string): boolean {
    const cleaned = (name ?? '').toLowerCase().replace(/\.$/, '');
    return cleaned === domain.toLowerCase() || cleaned === '' || cleaned === '@';
  }

  function customerCanEdit(r: DnsZoneRecord): boolean {
    if (mode === 'admin') return true;
    if (r.type === 'NS' && isApex(r.name)) return false;
    return true;
  }

  async function executeMutate(
    action: 'add' | 'edit' | 'delete',
    record?: DnsMutationRecord,
    line?: number,
  ): Promise<void> {
    setSubmitting(true);
    try {
      await domainService.dnsMutate({ domain, action, record, line });
      await refetch();
      toast({
        tone: 'success',
        title:
          action === 'add' ? 'Record added' : action === 'edit' ? 'Record updated' : 'Record deleted',
        body:
          'DNS changes can take 5–60 minutes to propagate to other resolvers.',
      });
    } catch (e) {
      toast({
        tone: 'danger',
        title: 'DNS update failed',
        body: e instanceof Error ? e.message : String(e),
      });
      throw e;
    } finally {
      setSubmitting(false);
    }
  }

  const editingIsLastMx = deleting?.type === 'MX' && mxRecords.length === 1;
  const editingIsApexA =
    (editing?.type === 'A' && isApex(editing.name)) ||
    (adding === false ? false : false); // apex-A-warning shown inside the form for new records

  return (
    <div className="space-y-4">
      {error && <NotificationCard tone="danger" title="Could not load DNS zone" body={error.message} />}

      {data?.warning && (
        <NotificationCard
          tone={data.ns_state === 'external' ? 'warning' : 'info'}
          title={
            data.ns_state === 'domains_coza_managed'
              ? 'Managed-DNS not yet editable here'
              : 'DNS not hosted by SightHost'
          }
          body={data.warning}
        />
      )}

      <Card interactive={false}>
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-navy-primary">DNS records</h3>
            <p className="text-xs text-muted-foreground">
              {data
                ? `${records.length} record${records.length === 1 ? '' : 's'} for ${domain}.`
                : 'Loading…'}
              {data?.editable === false && ' Read-only.'}
            </p>
            {/* Zone export is staff-only (owner decision 2026-07-26). Without
                this hint a customer has no way to know a copy is available at
                all. Seeds the GENERAL 'Domains' department (24h SLA) — NOT the
                lowercase 'domains' DT-1 execute queue, which is machine-driven
                and where a hand-filed ticket is invisible to the action queue. */}
            {mode === 'customer' && records.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Need a copy of your zone file?{' '}
                <Link
                  to={`/app/support/new?dept=Domains&subject=${encodeURIComponent(
                    `DNS zone export request — ${domain}`,
                  )}&body=${encodeURIComponent(
                    `Please send me an export of the DNS zone for ${domain}.`,
                  )}`}
                  className="font-medium text-navy-primary underline underline-offset-2 hover:text-primary-ink"
                >
                  Request it from support
                </Link>
                .
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Zone export is STAFF-ONLY (owner decision 2026-07-26). Customers
                who want a copy of their zone raise a support ticket, so the
                request is seen, actioned and recorded like any other domain
                request. Export itself is read-only and stays available even
                when the zone is not editable here (external NS) — taking a
                backup of a zone you cannot edit is exactly when you want one. */}
            {mode === 'admin' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadZoneFile(domain, records)}
                  disabled={records.length === 0}
                  title="Download a BIND zone file — importable by most DNS providers"
                >
                  <Download className="size-4" />
                  Zone file
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadZoneCsv(domain, records)}
                  disabled={records.length === 0}
                  title="Download all records as CSV"
                >
                  <Download className="size-4" />
                  CSV
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => setAdding(true)} disabled={!editable || submitting}>
              <Plus className="size-4" />
              Add record
            </Button>
          </div>
        </header>

        {isLoading && (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" /> Loading zone…
          </div>
        )}

        {!isLoading && records.length === 0 && (
          <p className="p-10 text-center text-sm text-muted-foreground">
            No editable DNS records for this domain.
          </p>
        )}

        {records.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Value</th>
                  <th className="px-3 py-2 text-right">TTL</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {records.map((r) => {
                  const apexLocked = mode === 'customer' && r.type === 'NS' && isApex(r.name);
                  return (
                    <tr key={r.line}>
                      <td className="px-3 py-2">
                        <StatusBadge tone="info">{String(r.type)}</StatusBadge>
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums text-navy-primary">
                        {isApex(r.name) ? '@' : r.name.replace(`.${domain}.`, '').replace(/\.$/, '')}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums text-foreground-soft">
                        {renderValueDisplay(r)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{r.ttl}s</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!editable || apexLocked || submitting}
                            onClick={() => setEditing(r)}
                            title={apexLocked ? 'Use the Nameservers tab to edit apex NS' : undefined}
                          >
                            <Pencil className="size-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!editable || apexLocked || submitting}
                            onClick={() => setDeleting(r)}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {editable && (
          <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
            DNS changes can take 5–60 minutes to propagate to other resolvers worldwide.
            {mode === 'customer' && ' Customers cannot edit apex NS records — use the Nameservers tab instead.'}
          </p>
        )}
      </Card>

      {(adding || editing) && (
        <RecordModal
          open
          mode={editing ? 'edit' : 'add'}
          domain={domain}
          customerMode={mode === 'customer'}
          initial={editing}
          submitting={submitting}
          onCancel={() => { setAdding(false); setEditing(null); }}
          onSubmit={async (record) => {
            try {
              if (editing) {
                await executeMutate('edit', record, editing.line);
                setEditing(null);
              } else {
                await executeMutate('add', record);
                setAdding(false);
              }
            } catch { /* toast already shown */ }
          }}
          showApexAWarning={editingIsApexA}
        />
      )}

      {deleting && (
        <DeleteConfirmModal
          open
          record={deleting}
          isLastMx={editingIsLastMx}
          submitting={submitting}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await executeMutate('delete', undefined, deleting.line);
              setDeleting(null);
            } catch { /* toast already shown */ }
          }}
        />
      )}
    </div>
  );
}

function renderValueDisplay(r: DnsZoneRecord): React.ReactNode {
  switch (String(r.type)) {
    case 'MX':
      return (
        <span>
          <span className="text-muted-foreground">{r.priority ?? 0}</span>{' '}
          <span className="break-all">{r.value}</span>
        </span>
      );
    case 'SRV':
      return (
        <span className="break-all">
          <span className="text-muted-foreground">{r.priority ?? 0}/{r.weight ?? 0}/{r.port ?? 0}</span>{' '}
          {r.value}
        </span>
      );
    case 'CAA':
      return (
        <span className="break-all">
          <span className="text-muted-foreground">{r.flag ?? 0} {r.tag ?? ''}</span>{' '}
          {r.value}
        </span>
      );
    default:
      return <span className="break-all">{r.value}</span>;
  }
}

// ─── Add / Edit modal ─────────────────────────────────────────────────

function RecordModal({
  open,
  mode,
  domain,
  customerMode,
  initial,
  submitting,
  onCancel,
  onSubmit,
  showApexAWarning,
}: {
  open: boolean;
  mode: 'add' | 'edit';
  domain: string;
  customerMode: boolean;
  initial: DnsZoneRecord | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (record: DnsMutationRecord) => Promise<void>;
  showApexAWarning?: boolean;
}) {
  const [type, setType] = useState<DnsRecordTypeFull>(
    (initial?.type as DnsRecordTypeFull) ?? 'A',
  );
  const [name, setName] = useState<string>(() => {
    if (!initial) return '';
    const cleaned = (initial.name ?? '').toLowerCase().replace(/\.$/, '');
    if (cleaned === domain.toLowerCase() || cleaned === '') return '@';
    return cleaned.replace(`.${domain.toLowerCase()}`, '');
  });
  const [value, setValue] = useState<string>(initial?.value ?? '');
  const [ttl, setTtl] = useState<number>(initial?.ttl ?? 14400);
  const [priority, setPriority] = useState<number>(initial?.priority ?? 10);
  const [weight, setWeight] = useState<number>(initial?.weight ?? 0);
  const [port, setPort] = useState<number>(initial?.port ?? 0);
  const [flag, setFlag] = useState<number>(initial?.flag ?? 0);
  const [tag, setTag] = useState<string>(initial?.tag ?? 'issue');
  const [error, setError] = useState<string | null>(null);

  const isApex = name === '' || name === '@' || name.toLowerCase() === domain.toLowerCase();
  const apexAWarn = type === 'A' && isApex;
  const customerNsBlocked = customerMode && type === 'NS' && isApex;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!value.trim()) {
      setError('Value is required.');
      return;
    }
    if (customerNsBlocked) {
      setError('Customers cannot edit apex NS records — use the Nameservers tab.');
      return;
    }

    // Normalise name back into the form WHM expects: bare label or @ for apex
    const normalisedName = name === '@' || name === '' ? '@' : name;

    const record: DnsMutationRecord = {
      type,
      name: normalisedName,
      value: value.trim(),
      ttl,
    };
    if (type === 'MX') record.priority = priority;
    if (type === 'SRV') {
      record.priority = priority;
      record.weight = weight;
      record.port = port;
    }
    if (type === 'CAA') {
      record.flag = flag;
      record.tag = tag;
    }

    await onSubmit(record);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onCancel()}
      title={mode === 'edit' ? `Edit DNS record · ${domain}` : `Add DNS record · ${domain}`}
      description="Changes can take 5–60 minutes to propagate."
      size="wide"
    >
      <form onSubmit={submit}>
        <ModalBody>
          {(apexAWarn || showApexAWarning) && (
            <NotificationCard
              tone="warning"
              title="Editing the apex A record may take your site offline"
              body={`The apex A record for ${domain} controls where browsers go when someone types just "${domain}". Changing it points the site to a different IP — make sure that's what you want.`}
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
            <FormField label="Type" htmlFor="dns-type">
              <select
                id="dns-type"
                value={type}
                onChange={(e) => setType(e.target.value as DnsRecordTypeFull)}
                disabled={submitting || mode === 'edit'}
                className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {EDITABLE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Name" htmlFor="dns-name" helper={`Use "@" for the apex (${domain} itself).`}>
              <Input
                id="dns-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="@ or subdomain"
                disabled={submitting}
              />
            </FormField>

            <FormField
              label="Value"
              htmlFor="dns-value"
              helper={valueHelperFor(type)}
              className="sm:col-span-2"
            >
              {type === 'TXT' ? (
                <textarea
                  id="dns-value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  rows={3}
                  disabled={submitting}
                  className="w-full rounded-md border border-input bg-card px-3 py-2 font-mono text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              ) : (
                <Input
                  id="dns-value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={submitting}
                  className="font-mono text-xs"
                />
              )}
            </FormField>

            {type === 'MX' && (
              <FormField label="Priority" htmlFor="dns-pri" helper="Lower = preferred (e.g. 10).">
                <Input
                  id="dns-pri"
                  type="number"
                  value={String(priority)}
                  onChange={(e) => setPriority(Number(e.target.value || 0))}
                  disabled={submitting}
                />
              </FormField>
            )}

            {type === 'SRV' && (
              <>
                <FormField label="Priority" htmlFor="dns-pri">
                  <Input id="dns-pri" type="number" value={String(priority)} onChange={(e) => setPriority(Number(e.target.value || 0))} disabled={submitting} />
                </FormField>
                <FormField label="Weight" htmlFor="dns-weight">
                  <Input id="dns-weight" type="number" value={String(weight)} onChange={(e) => setWeight(Number(e.target.value || 0))} disabled={submitting} />
                </FormField>
                <FormField label="Port" htmlFor="dns-port">
                  <Input id="dns-port" type="number" value={String(port)} onChange={(e) => setPort(Number(e.target.value || 0))} disabled={submitting} />
                </FormField>
              </>
            )}

            {type === 'CAA' && (
              <>
                <FormField label="Flag" htmlFor="dns-flag" helper="0 (default) or 128 (critical).">
                  <Input id="dns-flag" type="number" value={String(flag)} onChange={(e) => setFlag(Number(e.target.value || 0))} disabled={submitting} />
                </FormField>
                <FormField label="Tag" htmlFor="dns-tag" helper="issue / issuewild / iodef">
                  <select
                    id="dns-tag"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm"
                  >
                    <option value="issue">issue</option>
                    <option value="issuewild">issuewild</option>
                    <option value="iodef">iodef</option>
                  </select>
                </FormField>
              </>
            )}

            <FormField label="TTL (seconds)" htmlFor="dns-ttl" helper="60 to 86400.">
              <Input
                id="dns-ttl"
                type="number"
                value={String(ttl)}
                onChange={(e) => setTtl(Number(e.target.value || 14400))}
                min={60}
                max={86400}
                disabled={submitting}
              />
            </FormField>
          </div>

          {customerNsBlocked && (
            <NotificationCard
              tone="danger"
              title="Apex NS not editable here"
              body="Customers cannot edit apex NS records via this editor — use the Nameservers tab to change nameservers."
            />
          )}

          {error && (
            <div role="alert" className="mt-3 flex items-start gap-3 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting || customerNsBlocked}>
            {mode === 'edit' ? 'Save changes' : 'Add record'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function valueHelperFor(type: DnsRecordTypeFull): string {
  switch (type) {
    case 'A': return 'IPv4 address (e.g. 192.0.2.1).';
    case 'AAAA': return 'IPv6 address (e.g. 2001:db8::1).';
    case 'CNAME': return 'Hostname (e.g. example.com or app.host.com).';
    case 'MX': return 'Mail server hostname (e.g. mx1.example.com).';
    case 'TXT': return 'Free-form text (e.g. "v=spf1 include:_spf.google.com ~all").';
    case 'NS': return 'Nameserver hostname.';
    case 'SRV': return 'Target hostname.';
    case 'CAA': return 'Certificate authority value (e.g. "letsencrypt.org").';
    default: return '';
  }
}

// ─── Delete confirm modal ─────────────────────────────────────────────

function DeleteConfirmModal({
  open,
  record,
  isLastMx,
  submitting,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  record: DnsZoneRecord;
  isLastMx: boolean;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [confirmText, setConfirmText] = useState('');
  const requiresType = isLastMx;
  const canConfirm = !requiresType || confirmText.trim().toUpperCase() === 'DELETE EMAIL';

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onCancel()}
      title="Delete DNS record"
      size="default"
    >
      <ModalBody>
        {isLastMx && (
          <NotificationCard
            tone="danger"
            title="This is your last MX record"
            body="Deleting it will stop email delivery for this domain. Your inbox will start bouncing emails immediately. Make sure this is what you want."
          />
        )}
        <p className="mt-3 text-sm text-foreground">
          Delete this record?
        </p>
        <div className="mt-2 rounded-md bg-card-subtle p-3 text-xs">
          <div><strong>Type:</strong> {String(record.type)}</div>
          <div><strong>Name:</strong> <span className="tabular-nums">{record.name}</span></div>
          <div><strong>Value:</strong> <span className="break-all tabular-nums">{record.value}</span></div>
        </div>
        {requiresType && (
          <FormField
            label='Type "DELETE EMAIL" to confirm'
            htmlFor="confirm-text"
            className="mt-3"
          >
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={submitting}
            />
          </FormField>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => { void onConfirm(); }}
          loading={submitting}
          disabled={submitting || !canConfirm}
          variant={isLastMx ? 'outline' : 'primary'}
          className={isLastMx ? 'border-danger text-danger hover:bg-danger/10' : ''}
        >
          {isLastMx ? (
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="size-3.5" /> Delete and break email
            </span>
          ) : (
            'Delete record'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
